import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';

/**
 * Row types that appear on five of the nine agent pages (spec 16 §3.3–3.4).
 *
 * These are here rather than in `controls.tsx` because they are whole rows with
 * their own reasoning, not controls that sit on the right of one.
 */

/**
 * A locked row — its label, **its reason**, and a lock plus the word `Locked` or
 * `Never` where the control would be.
 *
 * **Not a disabled toggle.** A disabled toggle reads as *broken*; a lock reads as
 * *decided*. The reason text ships: these are the things a practice would
 * otherwise ask for and be refused without explanation, and on these pages the
 * explanation is the feature.
 */
export function SetLockedRow({
  label,
  reason,
  word = 'Locked',
  last,
  tone = 'plain',
}: {
  label: React.ReactNode;
  reason?: React.ReactNode;
  /** `Locked` where a dependency decides it, `Never` where ELVA does. */
  word?: string;
  last?: boolean;
  /** `red` on the never-say lists, where the card itself is the warning. */
  tone?: 'plain' | 'red';
}) {
  return (
    <div
      className="flex items-start justify-between gap-6 px-6 py-[15px]"
      style={last ? undefined : { borderBottom: '1px solid #edf0f6' }}
    >
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'text-[13.5px]',
            tone === 'red' ? 'text-[var(--text-secondary)]' : 'font-semibold text-[var(--text-primary)]',
          )}
        >
          {label}
        </div>
        {reason ? (
          <div className="mt-1 max-w-[620px] text-[12px] leading-[1.6] text-[var(--text-muted)]">{reason}</div>
        ) : null}
      </div>
      <span className="inline-flex shrink-0 items-center gap-1.5 text-[11.5px] font-semibold text-[var(--text-muted)]">
        <Icon kind="lock" size={12} stroke={2} />
        {word}
      </span>
    </div>
  );
}

/**
 * A plain fixed statement — a list item on `what a check may never do`, or a
 * guardrail. No control at all, because there was never going to be one.
 */
export function SetFixedRow({ children, last }: { children: React.ReactNode; last?: boolean }) {
  return (
    <div
      className="flex items-start gap-2.5 px-6 py-[11px] text-[13px] leading-[1.6] text-[var(--text-secondary)]"
      style={last ? undefined : { borderBottom: '1px solid #edf0f6' }}
    >
      <Icon kind="check" size={13} stroke={2.4} className="mt-[3px] shrink-0 text-[var(--money-green)]" />
      <span className="min-w-0">{children}</span>
    </div>
  );
}

/**
 * The routing row — "set once, somewhere else" (§3.4).
 *
 * Names a thing people come to an agent page looking for, says where it actually
 * lives, and links. **Where there is no destination the right-hand side reads
 * `Not configurable` as muted text, not a link** — that is a more useful answer
 * than an absent row, which just leaves somebody hunting.
 */
export function SetRoutingRow({
  label,
  desc,
  to,
  toLabel,
  last,
}: {
  label: React.ReactNode;
  desc?: React.ReactNode;
  /** Omit for something that genuinely has nowhere to go. */
  to?: string;
  toLabel?: string;
  last?: boolean;
}) {
  return (
    <div
      className="flex items-start justify-between gap-6 px-6 py-[15px]"
      style={last ? undefined : { borderBottom: '1px solid #edf0f6' }}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold text-[var(--text-primary)]">{label}</div>
        {desc ? <div className="mt-1 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">{desc}</div> : null}
      </div>
      {to !== undefined && toLabel !== undefined ? (
        <a
          href={to}
          className="shrink-0 text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
        >
          {toLabel} →
        </a>
      ) : (
        <span className="shrink-0 text-[12.5px] font-semibold text-[var(--text-muted)]">Not configurable</span>
      )}
    </div>
  );
}

/**
 * A read-only value where the control would be, with an optional caption naming
 * where it is really set. Used wherever a page shows a figure it does not own.
 */
export function SetReadOnly({ value, from }: { value: React.ReactNode; from?: React.ReactNode }) {
  return (
    <span className="inline-block text-right">
      <span className="block font-[var(--font-mono)] text-[13.5px] font-semibold text-[var(--text-primary)]">
        {value}
      </span>
      {from ? <span className="block text-[10.5px] text-[var(--text-muted)]">{from}</span> : null}
    </span>
  );
}

/**
 * A numbered, read-only list — the ranking factors on Gap Filler. Deterministic
 * and in order, with no weights to tune, so every position can be explained.
 */
export function SetNumberedList({ items }: { items: React.ReactNode[] }) {
  return (
    <ol className="m-0 flex list-none flex-col gap-2 p-0">
      {items.map((item, i) => (
        // The position IS the identity here — the list is numbered.
        <li key={i} className="flex items-start gap-2.5">
          <span className="mt-[1px] shrink-0 rounded-[6px] bg-[#F1F3F9] px-1.5 py-0.5 font-[var(--font-mono)] text-[11px] font-bold text-[var(--text-muted)]">
            {i + 1}
          </span>
          <span className="min-w-0 text-[13px] leading-[1.6] text-[var(--text-secondary)]">{item}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * A sub-panel inside a card body — the two filter groups on the TC, the
 * guardrail and ranking blocks on Gap Filler.
 */
export function SetSubCard({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-[#FAFBFE]" style={{ borderBottom: '1px solid #edf0f6' }}>
      <div className="px-6 pb-2 pt-[15px]">
        <div className="text-[13px] font-bold text-[var(--text-primary)]">{title}</div>
        {sub ? <div className="mt-1 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">{sub}</div> : null}
      </div>
      {children}
    </div>
  );
}
